import { useCallback, useEffect, useRef, useState } from 'react';
import sales from '../Sales';

const entries = new Map();
const listeners = new Set();
let seqCounter = 0;

const emit = () => listeners.forEach((listener) => listener());

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const peek = (url) => entries.get(url)?.data;

export const load = (url, { force = false } = {}) => {
  const entry = entries.get(url);
  if (entry?.promise && !force) return entry.promise;

  seqCounter += 1;
  const seq = seqCounter;
  const promise = sales.get(url).then(
    (response) => {
      if (entries.get(url)?.seq === seq) {
        entries.set(url, { data: response.data, seq });
        emit();
      }
      return response.data;
    },
    (error) => {
      if (entries.get(url)?.seq === seq) entries.set(url, { data: entries.get(url)?.data, seq });
      throw error;
    },
  );
  entries.set(url, { data: entry?.data, promise, seq });
  return promise;
};

export const refreshAll = () =>
  Promise.allSettled([...entries.keys()].map((url) => load(url, { force: true })));

export const clearStore = () => entries.clear();

export const useAdminData = (urls, { optional = [] } = {}) => {
  const key = JSON.stringify(urls);
  const urlsRef = useRef(urls);
  urlsRef.current = urls;
  const failed = useRef(new Set());

  const read = useCallback(() => {
    const out = {};
    for (const [name, url] of Object.entries(urlsRef.current)) {
      const value = peek(url);
      if (value === undefined) {
        if (optional.includes(name) && failed.current.has(name)) {
          out[name] = null;
          continue;
        }
        return null;
      }
      out[name] = value;
    }
    return out;
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const [data, setData] = useState(read);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sync = useCallback(() => {
    const next = read();
    if (!next) return;
    setData((previous) =>
      previous && Object.keys(next).every((name) => previous[name] === next[name]) ? previous : next,
    );
  }, [read]);

  useEffect(() => subscribe(sync), [sync]);

  const fetchAll = useCallback(
    (force) => {
      let active = true;
      setLoading(true);
      setError(null);
      Promise.all(
        Object.entries(urlsRef.current).map(([name, url]) =>
          load(url, { force }).catch((err) => {
            if (!optional.includes(name)) throw err;
            failed.current.add(name);
          }),
        ),
      )
        .then(() => {
          if (!active) return;
          sync();
          setLoading(false);
        })
        .catch((err) => {
          console.warn(err);
          if (!active) return;
          setError(err);
          setLoading(false);
        });
      return () => {
        active = false;
      };
    },
    [key, sync], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    setData(read());
    return fetchAll(false);
  }, [fetchAll, read]);

  const reload = useCallback(() => {
    fetchAll(true);
  }, [fetchAll]);

  return { data, loading, error, reload };
};
